import type { ReactElement } from 'react';
import type { SlotContents } from '@amplience/content-types/typings/s-pdp-layout';
import type { CarouselProduct } from '@storefront/lib-global/codegen/types';
import { createGlobalAmplienceComponent } from '@storefront/lib-global/globalComponents/globalAmplienceComponents.utils';
import type { HandlebarTemplateProps } from '@storefront/lib-global/globalComponents/handlebarTemplate';
import { isBusinessComponent } from '@storefront/lib-global/utils/amplienceUtils';
import { createPDPAmplienceComponent } from '../pdpAmplienceComponents.utils';
import { BelowTheFold } from './BelowTheFoldSection.styles';

export function BelowTheFoldSection({
  belowTheFoldContent,
  carouselData,
  singleProductKey,
  handlebarComponents,
  isZoomActive,
}: {
  belowTheFoldContent: SlotContents['belowTheFoldContent'];
  carouselData: { deliveryId: string; carouselProducts: CarouselProduct[] }[];
  singleProductKey: string;
  handlebarComponents: HandlebarTemplateProps[] | null;
  isZoomActive: boolean;
}) {
  if (!belowTheFoldContent) return null;

  const AmplienceComponents = {
    ...createGlobalAmplienceComponent(belowTheFoldContent as []),
    ...createPDPAmplienceComponent(belowTheFoldContent as []),
  };
  return (
    <BelowTheFold $isZoomActive={isZoomActive}>
      {belowTheFoldContent?.flatMap((comp, j) => {
        if (isBusinessComponent(comp) && handlebarComponents) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const handlebarItems = ((comp?.slotContent as any).components as Record<string, any>[]) ?? [];

          return handlebarItems?.flatMap((item, i) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const Component: (props: any) => ReactElement | null = AmplienceComponents['handlebar-component'];
            const { template } =
              handlebarComponents?.find((temp) => temp.input._meta.schema === item._meta.schema) ?? {};

            if (!template) return null;

            return (
              Component && (
                <Component input={item} template={template} key={`${item._meta.schema}-${j}-${i}`} index={j} />
              )
            );
          });
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Component: (props: any) => ReactElement | null = AmplienceComponents[comp._meta.schema];

        const carouselProducts =
          carouselData.find(({ deliveryId }) => deliveryId === comp._meta.deliveryId)?.carouselProducts ?? [];

        return (
          Component && (
            <Component
              {...comp}
              key={`${comp._meta.schema}-${j}`}
              carouselProducts={carouselProducts}
              singleProductKey={singleProductKey}
              index={j}
            />
          )
        );
      })}
    </BelowTheFold>
  );
}

export default BelowTheFoldSection;
